import {
  PaymentEmailBodyView,
  PaymentsAutoCollectFilterValue,
  PostmarkSenderSignatureView,
  RichSettings,
  SettingsPatch
} from "../../Contracts/Settings";
import { useRequestStore } from "../BackendStore";
import { create } from "zustand";
import { MultiGetResponse } from "../../Contracts/Common";
import { useBannerStore } from "../Banner";
import { BannerType } from "../../Contracts/Banner";

const settingsUrl = "/v1/payments/team/settings";
const settingsPatchUrl = "/v1/payments/team/settings";
const emailSettingsUrl = "/v1/payments/team/settings/email";
const updateEmailSettingsUrl = "/v1/payments/team/settings/email/<emailId>";

const getPostmarkSignatureUrl = "/v1/payments/team/settings/postmark";
const addPostmarkSignatureUrl = "/v1/payments/team/settings/postmark";
const deletePostmarkSignatureUrl = "/v1/payments/team/settings/postmark/<postmarkId>";
const checkPostmarkSignatureStatusUrl =
  "/v1/payments/team/settings/postmark/checkStatus/<postmarkId>/";
const getPaymentAutoCollectFilterValuesUrl = "/v1/payments/customers/filter-values";
const createPaymentAutoCollectFilterValuesUrl = "/v1/payments/customers/filter-values";
const deletePaymentAutoCollectFilterValuesUrl = "/v1/payments/customers/filter-values/<filterId>";

interface SettingsState {
  isLoading: boolean;
  settings?: RichSettings;
  getSettings: (getAccessTokenSilently: any) => Promise<void>;
  updateSettings: (getAccessTokenSilently: any, settingsPatch: SettingsPatch) => Promise<boolean>;
  emailBodyViews: PaymentEmailBodyView[];
  getEmailsSettings: (getAccessTokenSilently: any) => Promise<void>;
  updateEmailBodyViews: (
    getAccessTokenSilently: any,
    paymentEmailBodyView: PaymentEmailBodyView
  ) => Promise<void>;
  getPostmarkSignatures: (getAccessTokenSilently: any) => Promise<void>;
  deletePostmarkSignature: (getAccessTokenSilently: any, postmarkId: number) => Promise<void>;
  checkPostmarkSignatureStatus: (
    getAccessTokenSilently: any,
    postmarkId?: number
  ) => Promise<void>;
  createPostmarkSignature: (
    getAccessTokenSilently: any,
    senderEmail: string,
    returnPath: string
  ) => Promise<number>;
  paymentAutoCollectFilterValues: {
    [customerId: number]: PaymentsAutoCollectFilterValue[];
  };
  paymentAutoCollectFilterValuesCount: {
    [customerId: number]: number;
  };
  getPaymentAutoCollectFilterValues: (
    getAccessTokenSilently: any,
    customerId: string,
    limit?: number,
    sort?: string,
    page?: number
  ) => Promise<void>;
  createPaymentAutoCollectFilterValues: (
    getAccessTokenSilently: any,
    customerId: number,
    filter: string
  ) => Promise<void>;
  deletePaymentAutoCollectFilterValues: (
    getAccessTokenSilently: any,
    filterId: number
  ) => Promise<void>;
}

export const useSettingsStore = create<SettingsState>((set, get) => {
  const { getRequest, postRequest, putRequest, deleteRequest } = useRequestStore.getState();
  const { addBanner } = useBannerStore.getState();

  const getSettings = async (getAccessTokenSilently: any) => {
    set({ isLoading: true });
    try {
      const response = await getRequest<RichSettings>(getAccessTokenSilently, settingsUrl);
      set({ settings: response, isLoading: false });
    } catch (error) {
      console.error("Error fetching settings", error);
      set({ isLoading: false });
    }
  };

  const updateSettings = async (getAccessTokenSilently: any, settingsPatch: SettingsPatch) => {
    set({ isLoading: true });
    try {
      const response = await putRequest<SettingsPatch, RichSettings>(
        getAccessTokenSilently,
        settingsPatchUrl,
        settingsPatch
      );
      if (response.errors.length === 0) {
        addBanner({
          type: BannerType.Success,
          message: "Settings saved successfully"
        });
      } else {
        response.errors.forEach(error => {
          addBanner({
            type: BannerType.Error,
            message: error.replace("Bad Request: ", "")
          });
        });
      }
      set({ settings: response, isLoading: false });
      return Promise.resolve(response.errors.length === 0);
    } catch (error) {
      console.error("Error updating settings", error);
      set({ isLoading: false });
    }
    return Promise.resolve(false);
  };

  const getEmailsSettings = async (getAccessTokenSilently: any) => {
    set({ isLoading: true });
    try {
      const response = await getRequest<MultiGetResponse<PaymentEmailBodyView>>(
        getAccessTokenSilently,
        emailSettingsUrl
      );
      set({ emailBodyViews: response.data, isLoading: false });
    } catch (error) {
      console.error("Error fetching settings", error);
      set({ isLoading: false });
    }
  };

  const updateEmailBodyViews = async (
    getAccessTokenSilently: any,
    paymentEmailBodyView: PaymentEmailBodyView
  ) => {
    set({ isLoading: true });
    try {
      const response = await putRequest<PaymentEmailBodyView, PaymentEmailBodyView>(
        getAccessTokenSilently,
        updateEmailSettingsUrl.replace("<emailId>", paymentEmailBodyView.id.toString()),
        paymentEmailBodyView
      );
      addBanner({
        type: BannerType.Success,
        message: "Email body updated successfully"
      });
      set({
        emailBodyViews: get().emailBodyViews.map(view => {
          if (view.id === response.id) {
            return response;
          }
          return view;
        }),
        isLoading: false
      });
    } catch (error) {
      console.error("Error updating settings", error);
      set({ isLoading: false });
    }
  };

  const getPostmarkSignatures = async (getAccessTokenSilently: any) => {
    try {
      const response = await getRequest<PostmarkSenderSignatureView[]>(
        getAccessTokenSilently,
        getPostmarkSignatureUrl
      );
      const existingSettings = get().settings;
      if (existingSettings) {
        set({
          settings: {
            ...existingSettings,
            postmark_sender_signatures: response
          }
        });
      }
    } catch (error) {
      console.error("Error fetching postmark signatures", error);
    }
  };

  const checkPostmarkSignatureStatus = async (
    getAccessTokenSilently: any,
    postmarkId?: number
  ) => {
    if (!postmarkId) {
      return;
    }
    try {
      const response = await getRequest<PostmarkSenderSignatureView>(
        getAccessTokenSilently,
        checkPostmarkSignatureStatusUrl.replace("<postmarkId>", postmarkId.toString())
      );
      const existingSettings = get().settings;
      if (existingSettings) {
        const { postmark_sender_signatures: signatures = [] } = existingSettings;
        set({
          settings: {
            ...existingSettings,
            postmark_sender_signatures: signatures.map(signature =>
              signature.id === response.id ? response : signature
            )
          }
        });
      }
    } catch (error) {
      console.error("Error checking postmark signature status", error);
    }
  };

  const createPostmarkSignature = async (
    getAccessTokenSilently: any,
    senderEmail: string,
    returnPath: string
  ) => {
    set({ isLoading: true });
    try {
      const response = await postRequest<
        { sender_email: string; return_path: string },
        {
          message: string;
          postmark_sender_signature_view: PostmarkSenderSignatureView;
        }
      >(getAccessTokenSilently, addPostmarkSignatureUrl, {
        sender_email: senderEmail,
        return_path: returnPath
      });

      const existingSettings = get().settings;
      if (existingSettings) {
        const { postmark_sender_signatures: signatures = [] } = existingSettings;
        const isSignaturePresent = signatures.some(
          signature => signature.sender_email === senderEmail
        );

        const updatedSignatures = isSignaturePresent
          ? signatures.map(signature =>
              signature.sender_email === senderEmail
                ? response.postmark_sender_signature_view
                : signature
            )
          : [response.postmark_sender_signature_view, ...signatures];

        set({
          settings: {
            ...existingSettings,
            postmark_sender_signatures: updatedSignatures
          },
          isLoading: false
        });

        addBanner({
          type: BannerType.Success,
          message: response.message
        });
      }
      return response.postmark_sender_signature_view.id;
    } catch (error) {
      console.error("Error creating postmark signature", error);
      set({ isLoading: false });
      return 0;
    }
  };

  const deletePostmarkSignature = async (getAccessTokenSilently: any, postmarkId: number) => {
    try {
      await deleteRequest(
        getAccessTokenSilently,
        deletePostmarkSignatureUrl.replace("<postmarkId>", postmarkId.toString())
      );
      const existingSettings = get().settings;
      if (existingSettings) {
        set({
          settings: {
            ...existingSettings,
            postmark_sender_signatures:
              get().settings?.postmark_sender_signatures.filter(
                signature => signature.id !== postmarkId
              ) || new Array<PostmarkSenderSignatureView>()
          }
        });
      }
      addBanner({
        type: BannerType.Success,
        message: "Email signature deleted successfully"
      });
    } catch (error) {
      console.error("Error deleting postmark signature", error);
    }
  };

  const getPaymentAutoCollectFilterValues = async (
    getAccessTokenSilently: any,
    customerId: string,
    limit?: number,
    sort?: string,
    page?: number
  ) => {
    set({ isLoading: true });
    try {
      const response = await getRequest<MultiGetResponse<PaymentsAutoCollectFilterValue>>(
        getAccessTokenSilently,
        getPaymentAutoCollectFilterValuesUrl,
        {
          filters: `customer_id:eq:${customerId}`,
          include_total_count: true,
          limit,
          sort,
          page
        }
      );

      const existingPaymentAutoCollectFilterValues = get().paymentAutoCollectFilterValues;
      const existingPaymentAutoCollectFilterValuesCount = get().paymentAutoCollectFilterValuesCount;
      set({
        paymentAutoCollectFilterValues: {
          ...existingPaymentAutoCollectFilterValues,
          [customerId]: response.data
        },
        paymentAutoCollectFilterValuesCount: {
          ...existingPaymentAutoCollectFilterValuesCount,
          [customerId]: response.filtered_count_map?.all || 0
        },
        isLoading: false
      });
    } catch (error) {
      console.error("Error fetching payment auto collect filter values", error);
      set({ isLoading: false });
    }
  };

  const createPaymentAutoCollectFilterValues = async (
    getAccessTokenSilently: any,
    customerId: number,
    filter: string
  ) => {
    set({ isLoading: true });
    try {
      const response = await postRequest<
        { filter: string; customer_id: number },
        PaymentsAutoCollectFilterValue
      >(getAccessTokenSilently, createPaymentAutoCollectFilterValuesUrl, {
        customer_id: customerId,
        filter
      });
      const existingPaymentAutoCollectFilterValues = get().paymentAutoCollectFilterValues;
      const existingPaymentAutoCollectFilterValuesCount = get().paymentAutoCollectFilterValuesCount;
      set({
        paymentAutoCollectFilterValues: {
          ...existingPaymentAutoCollectFilterValues,
          [customerId]: [response, ...(existingPaymentAutoCollectFilterValues[customerId] || [])]
        },
        paymentAutoCollectFilterValuesCount: {
          ...existingPaymentAutoCollectFilterValuesCount,
          [customerId]: (existingPaymentAutoCollectFilterValuesCount[customerId] || 0) + 1
        },
        isLoading: false
      });
      addBanner({
        type: BannerType.Success,
        message: "Payment auto collect filter value created successfully"
      });
    } catch (error) {
      console.error("Error creating payment auto collect filter values", error);
      set({ isLoading: false });
    }
  };

  const deletePaymentAutoCollectFilterValues = async (
    getAccessTokenSilently: any,
    filterId: number
  ) => {
    set({ isLoading: true });
    try {
      await deleteRequest(
        getAccessTokenSilently,
        deletePaymentAutoCollectFilterValuesUrl.replace("<filterId>", filterId.toString())
      );
      const existingPaymentAutoCollectFilterValues = get().paymentAutoCollectFilterValues;
      const existingPaymentAutoCollectFilterValuesCount = get().paymentAutoCollectFilterValuesCount;
      const customerId = Object.keys(existingPaymentAutoCollectFilterValues).find(customerId =>
        existingPaymentAutoCollectFilterValues[parseInt(customerId)].find(
          filter => filter.id === filterId
        )
      );
      if (customerId) {
        set({
          paymentAutoCollectFilterValues: {
            ...existingPaymentAutoCollectFilterValues,
            [parseInt(customerId)]: existingPaymentAutoCollectFilterValues[
              parseInt(customerId)
            ].filter(filter => filter.id !== filterId)
          },
          paymentAutoCollectFilterValuesCount: {
            ...existingPaymentAutoCollectFilterValuesCount,
            [parseInt(customerId)]:
              (existingPaymentAutoCollectFilterValuesCount[parseInt(customerId)] || 0) - 1
          },
          isLoading: false
        });
      }
      addBanner({
        type: BannerType.Success,
        message: "Payment auto collect filter value deleted successfully"
      });
    } catch (error) {
      console.error("Error deleting payment auto collect filter values", error);
      set({ isLoading: false });
    }
  };

  return {
    getSettings,
    updateSettings,
    getEmailsSettings,
    updateEmailBodyViews,
    getPostmarkSignatures,
    checkPostmarkSignatureStatus,
    createPostmarkSignature,
    deletePostmarkSignature,
    getPaymentAutoCollectFilterValues,
    createPaymentAutoCollectFilterValues,
    deletePaymentAutoCollectFilterValues,
    paymentAutoCollectFilterValues: {},
    paymentAutoCollectFilterValuesCount: {},
    emailBodyViews: [],
    isLoading: true
  };
});
